import React, { useMemo, useState, useCallback } from "react";
import { Search, ChevronDown, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { useToast } from "@/hooks/use-toast";
import { useEditorStore } from "@/stores/editor/editor-store";
import { templateInstantiationService } from "@/services/ai/TemplateInstantiationService";
import { getAllTemplates, type TemplateMetadata } from "@/utils/templateLoader";

interface TemplateBrowserProps {
  className?: string;
}

interface FolderGroup {
  id: string;
  label: string;
  templates: TemplateMetadata[];
}

function groupByFolder(templates: TemplateMetadata[]): FolderGroup[] {
  const map = new Map<string, FolderGroup>();
  for (const t of templates) {
    const existing = map.get(t.folder.id);
    if (existing) {
      existing.templates.push(t);
    } else {
      map.set(t.folder.id, { id: t.folder.id, label: t.folder.label, templates: [t] });
    }
  }
  return Array.from(map.values());
}

export const TemplateBrowser: React.FC<TemplateBrowserProps> = ({ className }) => {
  const { project, currentPageId, addComponent } = useEditorStore();
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState("");
  const [busyId, setBusyId] = useState<string | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set());

  const allTemplates = useMemo(() => getAllTemplates(), []);

  const filtered = useMemo(() => {
    if (!searchTerm.trim()) return allTemplates;
    const term = searchTerm.toLowerCase();
    return allTemplates.filter(
      (t) =>
        t.name.toLowerCase().includes(term) ||
        t.description.toLowerCase().includes(term) ||
        t.id.toLowerCase().includes(term),
    );
  }, [allTemplates, searchTerm]);

  const groups = useMemo(() => groupByFolder(filtered), [filtered]);

  const isSearching = searchTerm.trim().length > 0;

  const expandAll = useCallback(
    () => setExpandedIds(new Set(groups.map((g) => g.id))),
    [groups],
  );
  const collapseAll = useCallback(() => setExpandedIds(new Set()), []);

  const toggleGroup = useCallback((id: string, next: boolean) => {
    setExpandedIds((prev) => {
      const s = new Set(prev);
      next ? s.add(id) : s.delete(id);
      return s;
    });
  }, []);

  const handleInsert = useCallback(
    async (template: TemplateMetadata) => {
      if (!project?.id || !currentPageId) {
        toast({
          title: "No active project",
          description: "Please create or open a project first.",
          variant: "destructive",
        });
        return;
      }

      setBusyId(template.id);
      try {
        const instance = await templateInstantiationService.instantiateTemplate(
          template.id,
          project.id,
          currentPageId,
          { x: 80 + Math.random() * 80, y: 80 + Math.random() * 80 },
        );

        if (!instance) {
          toast({
            title: "Insert failed",
            description: `Could not load "${template.name}".`,
            variant: "destructive",
          });
          return;
        }

        for (const component of instance.components) {
          addComponent(component);
        }

        toast({
          title: "Template added",
          description: `${template.name} added. Use AI chat to customize it.`,
        });
      } catch (error) {
        console.error("[TemplateBrowser] instantiate failed:", error);
        toast({
          title: "Insert failed",
          description: error instanceof Error ? error.message : "Unexpected error.",
          variant: "destructive",
        });
      } finally {
        setBusyId(null);
      }
    },
    [project, currentPageId, addComponent, toast],
  );

  return (
    <div className={cn("h-full flex flex-col", className)}>
      {/* Search */}
      <div className="px-3 py-2 border-b">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input
            placeholder="Search templates..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-8 h-8 text-sm"
          />
        </div>
      </div>

      {/* Header row — count + expand/collapse */}
      <div className="flex items-center justify-between px-3 py-1.5 text-xs text-muted-foreground border-b">
        <span>{filtered.length} templates</span>
        {!isSearching && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="font-medium text-primary hover:underline"
              onClick={expandAll}
            >
              Expand all
            </button>
            <button
              type="button"
              className="font-medium text-primary hover:underline"
              onClick={collapseAll}
            >
              Collapse all
            </button>
          </div>
        )}
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto py-1">
        {groups.length === 0 ? (
          <p className="text-sm text-center text-muted-foreground mt-8 px-4">
            No templates match "{searchTerm}".
          </p>
        ) : (
          groups.map((group) => {
            const isOpen = isSearching || expandedIds.has(group.id);
            return (
              <Collapsible
                key={group.id}
                open={isOpen}
                onOpenChange={(next) => !isSearching && toggleGroup(group.id, next)}
              >
                <CollapsibleTrigger asChild>
                  <button
                    type="button"
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm font-semibold hover:bg-accent transition-colors"
                  >
                    {isOpen ? (
                      <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                    )}
                    <span className="flex-1">{group.label}</span>
                    <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                      {group.templates.length}
                    </span>
                  </button>
                </CollapsibleTrigger>

                <CollapsibleContent className="pb-1">
                  <div className="space-y-0.5 pl-4 pr-2">
                    {group.templates.map((template) => {
                      const isBusy = busyId === template.id;
                      return (
                        <button
                          key={template.id}
                          type="button"
                          disabled={isBusy}
                          onClick={() => handleInsert(template)}
                          className={cn(
                            "flex w-full items-start gap-2 rounded-sm px-2 py-2 text-left text-sm hover:bg-accent transition-colors",
                            isBusy && "opacity-60 cursor-wait",
                          )}
                        >
                          <span className="min-w-0 flex-1">
                            <span className="block truncate font-medium">{template.name}</span>
                            {template.description && (
                              <span className="line-clamp-2 text-xs text-muted-foreground">
                                {template.description}
                              </span>
                            )}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </CollapsibleContent>
              </Collapsible>
            );
          })
        )}
      </div>
    </div>
  );
};
